import { useState } from 'react';
import { api } from '../api';
import ModalRoot from './ModalRoot';

/**
 * Archetype-aware edit modal for a notification event's recipient filter.
 * Three archetypes supported: driver_shift_region, flat_role, relationship.
 */
export default function NotificationFilterModal({ event, moduleKey, onClose, onSaved }) {
  const [filter, setFilter] = useState(JSON.parse(JSON.stringify(event.filter || {})));
  const [saving, setSaving] = useState(false);

  function update(patch) {
    setFilter(f => ({ ...f, ...patch }));
  }

  async function save() {
    setSaving(true);
    try {
      await api.updateNotificationEventFilter(moduleKey, event.id, filter);
      if (onSaved) await onSaved();
    } catch (err) {
      alert('Save failed: ' + (err.message || 'unknown error'));
      setSaving(false);
    }
  }

  function resetToDefault() {
    if (!event.default_filter) return;
    if (!window.confirm('Reset this filter to its default?')) return;
    setFilter(JSON.parse(JSON.stringify(event.default_filter)));
  }

  return (
    <ModalRoot className="" style={backdropStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{event.label}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            {event.trigger_detail}
          </p>
        </div>

        <div style={{ padding: 20, maxHeight: '60vh', overflowY: 'auto' }}>
          {event.filter_archetype === 'driver_shift_region' && (
            <DriverShiftRegionEditor filter={filter} update={update} />
          )}
          {event.filter_archetype === 'flat_role' && (
            <FlatRoleEditor filter={filter} update={update} />
          )}
          {event.filter_archetype === 'relationship' && (
            <RelationshipEditor filter={filter} update={update} />
          )}
        </div>

        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--border-subtle)',
          display: 'flex', justifyContent: 'space-between', gap: 8, background: 'var(--bg-subtle)',
        }}>
          <button className="btn btn-muted btn-sm" onClick={resetToDefault} disabled={saving}>
            Reset to default
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-muted btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </ModalRoot>
  );
}

// ============================================================
// Archetype: driver_shift_region
// ============================================================
function DriverShiftRegionEditor({ filter, update }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="Which shift?" hint="Whose shift is checked when picking drivers.">
        <select
          value={filter.shift_source || 'current'}
          onChange={e => update({ shift_source: e.target.value })}
          className="form-input"
          style={{ width: '100%' }}
        >
          <option value="current">Current shift (wall clock)</option>
          <option value="due_time">Shift of the call's due time</option>
          <option value="incoming">Incoming shift (used for shift-change notifications)</option>
        </select>
      </Field>

      <Toggle
        label="Match call region"
        hint="Driver's regions must include the call's region (Fort Worth / Dallas / Denton)."
        checked={filter.match_region !== false}
        onChange={v => update({ match_region: v })}
      />

      <Toggle
        label="Match driver workday"
        hint="Driver's work_days must include the active day of the shift."
        checked={filter.match_workday !== false}
        onChange={v => update({ match_workday: v })}
      />

      <Toggle
        label="Emergency bypass"
        hint="When 🚨 Emergency is set on the call, ignore the shift filter so on-call and off-shift drivers also get SMS."
        checked={filter.emergency_override_shift === true}
        onChange={v => update({ emergency_override_shift: v })}
      />
    </div>
  );
}

// ============================================================
// Archetype: flat_role
// ============================================================
const ROLE_OPTIONS = [
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'admin', label: 'Admin' },
  { value: 'sales', label: 'Sales' },
  { value: 'driver', label: 'Driver' },
  { value: 'maintenance', label: 'Maintenance' },
];
const TIER_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'User' },
];

function FlatRoleEditor({ filter, update }) {
  const roles = filter.roles || [];
  const tiers = filter.tiers || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="Roles" hint="Users with any of these legacy roles receive the notification.">
        <CheckboxGrid
          options={ROLE_OPTIONS}
          selected={roles}
          onChange={v => update({ roles: v })}
        />
      </Field>

      <Field label="Permission tiers" hint="Additionally include users at any of these tiers.">
        <CheckboxGrid
          options={TIER_OPTIONS}
          selected={tiers}
          onChange={v => update({ tiers: v })}
        />
      </Field>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
        Position-level and specific-user targeting can be added later. Today only roles + tiers are wired.
      </p>
    </div>
  );
}

// ============================================================
// Archetype: relationship
// ============================================================
const RELATIONSHIP_TARGETS = [
  { value: 'call_creator', label: 'Call creator' },
  { value: 'sales_rep', label: 'Sales rep (on-behalf-of)' },
  { value: 'claimed_driver', label: 'Claimed driver' },
  { value: 'thread_participants', label: 'Thread participants (commenters + creator)' },
  { value: 'eligible_drivers_shift_region', label: 'Eligible drivers (shift/region match)' },
];

function RelationshipEditor({ filter, update }) {
  if (filter.conditional_rules) {
    // Comment event — show a matrix view, read-only for simplicity
    return (
      <div>
        <p style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 12 }}>
          This notification uses <strong>conditional routing</strong>. The recipients depend on
          the call's state and who commented:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
          {Object.entries(filter.conditional_rules).map(([scenario, targets]) => (
            <div key={scenario} style={{
              border: '1px solid var(--border-subtle)', borderRadius: 6, padding: 10,
              background: 'var(--bg-subtle)',
            }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                {formatScenario(scenario)}
              </div>
              <div>{targets.map(t => labelForTarget(t)).join(' + ') || '(none)'}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          Comment routing logic is fixed for safety. Toggle the event off if you need to silence it entirely.
        </p>
      </div>
    );
  }

  const selected = filter.targets || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="Recipients" hint="Users related to the call who should be notified.">
        <CheckboxGrid
          options={RELATIONSHIP_TARGETS}
          selected={selected}
          onChange={v => update({ targets: v })}
        />
      </Field>

      <Toggle
        label="Skip the user who triggered the event"
        hint="Don't notify the person who resolved / cancelled / commented — they already know."
        checked={filter.skip_self !== false}
        onChange={v => update({ skip_self: v })}
      />

      {/* Resolution-specific option */}
      {'skip_public_submissions' in filter && (
        <Toggle
          label="Skip public submissions"
          hint="Don't notify the creator of calls submitted through the public /request form."
          checked={filter.skip_public_submissions !== false}
          onChange={v => update({ skip_public_submissions: v })}
        />
      )}
    </div>
  );
}

function labelForTarget(t) {
  const opt = RELATIONSHIP_TARGETS.find(o => o.value === t);
  return opt ? opt.label : t;
}
function formatScenario(key) {
  return key.replace(/_/g, ' ').replace(/\bby\b/, 'by ');
}

// ============================================================
// Shared primitives
// ============================================================
function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      {hint && <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 8px' }}>{hint}</p>}
      {children}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange }) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ marginTop: 2, width: 16, height: 16, accentColor: 'var(--brand-gold)' }}
      />
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
      </div>
    </label>
  );
}

function CheckboxGrid({ options, selected, onChange }) {
  function toggle(val) {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
      {options.map(opt => (
        <label key={opt.value} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
          border: '1px solid var(--border-subtle)', borderRadius: 6,
          background: selected.includes(opt.value) ? 'rgba(218, 165, 32, 0.08)' : 'transparent',
          cursor: 'pointer', fontSize: 13,
        }}>
          <input
            type="checkbox"
            checked={selected.includes(opt.value)}
            onChange={() => toggle(opt.value)}
            style={{ width: 14, height: 14, accentColor: 'var(--brand-gold)' }}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle = {
  background: 'var(--surface-base, #fff)', borderRadius: 10, width: '90%', maxWidth: 600,
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)', display: 'flex', flexDirection: 'column',
};
